import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CurrencyDollar, Plus, Trash, Eye, EyeSlash } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, formatApiErrorDetail } from "../lib/api";
import { useEditConfirm } from "../context/EditConfirmContext";

const navItems = [{ to: "/admin/financial-tracker", label: "Financial Tracker", icon: <CurrencyDollar size={18} weight="duotone" />, testId: "nav-admin-financial-tracker" }];

function AdminFinancialOverviewTab() {
  const confirmEdit = useEditConfirm();
  const [data, setData] = useState(null);
  const [pending, setPending] = useState([]);

  const loadPending = () => {
    api.get("/admin/financial/pending-approvals").then(({ data }) => setPending(data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/admin/financial/overview")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    loadPending();
  }, []);

  const decideCommission = async (eventId, decision) => {
    const p = pending.find((x) => x.id === eventId);
    if (!(await confirmEdit(`${p?.rep_display_name || "this rep"}'s commission (${decision})`))) return;
    try {
      await api.post(`/admin/financial/${decision}/${eventId}`);
      toast.success(decision === "approve" ? "Commission approved for payout" : "Commission declined");
      loadPending();
      const { data: fresh } = await api.get("/admin/financial/overview");
      setData(fresh);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const setTier = async (repId, tier) => {
    const r = data?.reps?.find((x) => x.id === repId);
    if (!(await confirmEdit(`${r?.display_name || "this rep"}'s tier`))) return;
    try {
      await api.patch(`/admin/financial/reps/${repId}/tier`, { tier_override: tier });
      toast.success("Tier updated");
      const { data: fresh } = await api.get("/admin/financial/overview");
      setData(fresh);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!data) return null;

  return (
    <div data-testid="admin-financial-overview-tab" className="space-y-8">
      {pending.length > 0 && (
        <div>
          <h2 className="font-serif text-lg text-igc-cream tracking-tight mb-1" title="Commissions a rep earned that need your sign-off before they can be paid out.">Pending Commission Approvals</h2>
          <p className="text-igc-muted text-xs mb-3">Nothing pays out until you approve it here (or reply APPROVE on WhatsApp).</p>
          <div className="bg-igc-surface border border-igc-gold/30 divide-y divide-igc-gold/10" data-testid="admin-pending-approvals-list">
            {pending.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`admin-pending-approval-row-${p.id}`}>
                <div>
                  <span className="text-igc-cream">{p.rep_display_name}</span>
                  <span className="text-igc-muted text-xs ml-2 capitalize">{p.deal_type}{p.trust_product ? ` — ${p.trust_product}` : ""}</span>
                  {p.lender_fee != null && <span className="text-igc-muted text-xs ml-2">Lender Fee: ${p.lender_fee.toLocaleString()}</span>}
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-igc-goldText font-serif">${p.rep_commission_amount.toLocaleString()}</span>
                  <button data-testid={`admin-approve-commission-${p.id}`} onClick={() => decideCommission(p.id, "approve")} title="Approves this commission — it moves into the next payout run." className="bg-igc-success/15 text-igc-success hover:bg-igc-success/25 px-3 py-1.5 text-xs font-medium transition-colors">Approve</button>
                  <button data-testid={`admin-reject-commission-${p.id}`} onClick={() => decideCommission(p.id, "reject")} title="Declines this commission — the rep will not be paid this amount." className="bg-igc-danger/15 text-igc-danger hover:bg-igc-danger/25 px-3 py-1.5 text-xs font-medium transition-colors">Reject</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="admin-financial-lender-fees">
          <p className="text-igc-muted text-xs uppercase tracking-widest mb-2" title="Total fees charged by lenders across every funded deal.">Lender Fees</p>
          <p className="font-serif text-xl text-igc-cream">${data.totals.total_lender_fees.toLocaleString()}</p>
        </div>
        <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="admin-financial-rep-commission">
          <p className="text-igc-muted text-xs uppercase tracking-widest mb-2" title="Total commission earned by reps across every deal.">Rep Commission</p>
          <p className="font-serif text-xl text-igc-goldText">${data.totals.total_rep_commission.toLocaleString()}</p>
        </div>
        <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="admin-financial-manager-override">
          <p className="text-igc-muted text-xs uppercase tracking-widest mb-2" title="Total override commission earned by managers on their teams' deals.">Manager Override</p>
          <p className="font-serif text-xl text-igc-goldText">${data.totals.total_manager_override.toLocaleString()}</p>
        </div>
        <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="admin-financial-igc-retained">
          <p className="text-igc-muted text-xs uppercase tracking-widest mb-2" title="What IGC keeps after paying out rep and manager commissions.">IGC Retained</p>
          <p className="font-serif text-xl text-igc-success">${data.totals.igc_retained.toLocaleString()}</p>
        </div>
        <div className="bg-igc-surface border border-igc-gold/15 p-4" data-testid="admin-financial-mrr">
          <p className="text-igc-muted text-xs uppercase tracking-widest mb-2" title="Estimated monthly recurring revenue from active subscription trackers, plus how many are currently active.">Tracker MRR</p>
          <p className="font-serif text-xl text-igc-cream">${data.subscriptions.estimated_mrr.toLocaleString()}</p>
          <p className="text-igc-muted text-[10px] mt-1">{data.subscriptions.active_count} active subs</p>
        </div>
      </div>

      <div>
        <h2 className="font-serif text-lg text-igc-cream tracking-tight mb-3" title="Full Partner reps earn a higher commission tier than Starter reps. Normally this switches automatically at day 30, but you can force it either way here.">Rep Tiers</h2>
        <div className="bg-igc-surface border border-igc-gold/15 divide-y divide-igc-gold/10" data-testid="admin-financial-rep-tiers-list">
          {data.reps.map((r) => (
            <div key={r.id} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`admin-financial-rep-row-${r.id}`}>
              <div>
                <span className="text-igc-cream">{r.display_name}</span>
                <span className="text-igc-muted text-xs ml-2">{r.user_id}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2 py-1 ${r.tier === "full_partner" ? "bg-igc-success/10 text-igc-success" : "bg-igc-gold/15 text-igc-goldText"}`}>{r.tier === "full_partner" ? "Full Partner" : "Starter"}</span>
                <Select value={r.tier_override || "auto"} onValueChange={(v) => setTier(r.id, v === "auto" ? null : v)}>
                  <SelectTrigger data-testid={`admin-financial-tier-select-${r.id}`} title="Auto lets the system decide based on the rep's tenure (Day 30 switch). Force overrides that manually." className="w-40 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream text-xs h-8"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                    <SelectItem value="auto">Auto (Day 30)</SelectItem>
                    <SelectItem value="starter">Force Starter</SelectItem>
                    <SelectItem value="full_partner">Force Full Partner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="font-serif text-lg text-igc-cream tracking-tight mb-3" title="Every individual commission-generating event logged in the system, with its current payout status.">Commission Events</h2>
        <div className="bg-igc-surface border border-igc-gold/15 divide-y divide-igc-gold/10 max-h-[500px] overflow-y-auto" data-testid="admin-financial-events-list">
          {data.events.length === 0 ? (
            <p className="text-igc-muted text-sm px-4 py-3" data-testid="admin-financial-no-events">No commission events logged yet.</p>
          ) : data.events.map((e) => (
            <div key={e.id} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`admin-financial-event-row-${e.id}`}>
              <div>
                <span className="text-igc-cream">{e.rep_display_name}</span>
                <span className="text-igc-muted text-xs ml-2 capitalize">{e.deal_type}{e.trust_product ? ` — ${e.trust_product}` : ""}</span>
                <span className="text-igc-muted text-xs ml-2">{e.created_at ? new Date(e.created_at).toLocaleDateString() : ""}</span>
              </div>
              <div className="flex items-center gap-4 text-xs">
                {e.lender_fee != null && <span className="text-igc-muted">Lender Fee: <span className="text-igc-cream">${e.lender_fee.toLocaleString()}</span></span>}
                <span className="text-igc-muted">Rep: <span className="text-igc-goldText">${e.rep_commission_amount.toLocaleString()}</span></span>
                {e.manager_override_amount > 0 && <span className="text-igc-muted">Mgr: <span className="text-igc-goldText">${e.manager_override_amount.toLocaleString()}</span></span>}
                <span className={`px-2 py-1 capitalize ${e.status === "paid" ? "bg-igc-success/10 text-igc-success" : "bg-igc-gold/15 text-igc-goldText"}`}>{e.status}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AdminMotivationMessagesTab() {
  const confirmEdit = useEditConfirm();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/motivation-messages").then(({ data }) => setMessages(data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  };

  useEffect(load, []);

  const create = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setSaving(true);
    try {
      await api.post("/admin/motivation-messages", { message: newMessage.trim() });
      setNewMessage("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id, active) => {
    if (!(await confirmEdit("this motivation message"))) return;
    try {
      await api.patch(`/admin/motivation-messages/${id}`, { active: !active });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/motivation-messages/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-motivation-messages-tab" className="max-w-2xl">
      <p className="text-igc-muted text-sm mb-4">These rotate through the motivation banner on every Rep and Manager dashboard, alongside the live commission leaderboard.</p>
      <form onSubmit={create} className="flex gap-3 mb-6">
        <Input data-testid="admin-motivation-new-message" placeholder="e.g. Keep pushing — every call counts!" title="Write a short motivational line — it'll rotate into the banner reps and managers see on their dashboards." value={newMessage} onChange={(e) => setNewMessage(e.target.value)} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream flex-1" />
        <Button data-testid="admin-motivation-add-button" type="submit" disabled={saving} title="Adds this message to the rotation." className="bg-igc-gold hover:bg-igc-goldHover text-black"><Plus size={16} weight="bold" className="mr-1" /> Add</Button>
      </form>
      <div className="bg-igc-surface border border-igc-gold/15 divide-y divide-igc-gold/10" data-testid="admin-motivation-messages-list">
        {messages.length === 0 ? (
          <p className="text-igc-muted text-sm px-4 py-3" data-testid="admin-motivation-no-messages">No messages yet — add one above.</p>
        ) : messages.map((m) => (
          <div key={m.id} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`admin-motivation-message-row-${m.id}`}>
            <span className={m.active ? "text-igc-cream" : "text-igc-muted line-through"}>{m.message}</span>
            <div className="flex items-center gap-3">
              <button data-testid={`admin-motivation-toggle-${m.id}`} onClick={() => toggleActive(m.id, m.active)} title="Turns this message on or off in the rotation without deleting it." className="text-igc-muted hover:text-igc-goldText">
                {m.active ? <Eye size={16} weight="bold" /> : <EyeSlash size={16} weight="bold" />}
              </button>
              <button data-testid={`admin-motivation-delete-${m.id}`} onClick={() => remove(m.id)} title="Permanently deletes this message." className="text-igc-muted hover:text-igc-danger">
                <Trash size={16} weight="bold" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminFinancialTrackerPage() {
  return (
    <DashboardLayout navItems={navItems} moduleName="Financial Tracker" moduleBackPath="/admin">
      <div data-testid="admin-financial-tracker-page">
        <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-2">Financial Tracker</h1>
        <p className="text-igc-muted text-sm mb-8">Commission engine oversight, rep tiers, subscriptions, and the rep motivation banner.</p>
        <Tabs defaultValue="overview">
          <TabsList className="bg-igc-surface border border-igc-gold/15 mb-6">
            <TabsTrigger data-testid="admin-financial-tab-overview-trigger" value="overview" className="text-igc-muted data-[state=active]:bg-igc-gold/15 data-[state=active]:text-igc-goldText">Overview</TabsTrigger>
            <TabsTrigger data-testid="admin-financial-tab-motivation-trigger" value="motivation" className="text-igc-muted data-[state=active]:bg-igc-gold/15 data-[state=active]:text-igc-goldText">Motivation Banner</TabsTrigger>
          </TabsList>
          <TabsContent value="overview"><AdminFinancialOverviewTab /></TabsContent>
          <TabsContent value="motivation"><AdminMotivationMessagesTab /></TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
